/**
 * Словари интерфейса и форматирование.
 *
 * Точка входа: экраны импортируют `../i18n`, ничего не зная о том, что
 * словари лежат в отдельных файлах. Сами словари — в `ru.ts` и `uz.ts`.
 */
import type { Locale, OrderStatus } from '@arbuz/shared';
import { ru, type Key } from './ru';
import { uz } from './uz';

export type { Key };
export { ru, uz };

/**
 * Переводчик на локаль — один и тот же объект на всё время жизни приложения.
 *
 * Постоянство идентичности здесь не оптимизация, а контракт: `t` попадает в
 * деплисты `useCallback` и `useEffect` на трёх десятках экранов. Новое
 * замыкание на каждый рендер превращало загрузку данных в бесконечный цикл
 * запросов — кабинет исполнителя выбирал лимит 200/мин и вставал в 429.
 */
const translators: Record<Locale, (key: Key) => string> = {
  ru: (key) => ru[key],
  // Полноту узбекского словаря держит его собственный тип `Record<Key, string>`;
  // откат на русскую строку остаётся страховкой на случай ключа из рантайма.
  uz: (key) => uz[key] ?? ru[key],
};

export function makeT(locale: Locale): (key: Key) => string {
  return translators[locale];
}

/**
 * Подпись статуса заказа.
 *
 * Одна на клиента и курьера: раньше таблица статусов жила прямо в `MyOrders`,
 * и второй экран неизбежно завёл бы свою копию.
 *
 * Цвет не единственный носитель смысла — подпись обязательна рядом с ним.
 */
export function orderStatusLabel(status: OrderStatus, locale: Locale): string {
  return makeT(locale)(`status_${status}` as Key);
}

/**
 * Формат суммы. Деньги приходят строкой из numeric(12,2) — в число не
 * приводим до самого отображения, чтобы не потерять копейки по дороге.
 */
export function formatMoney(value: string, locale: Locale): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  // Копейки в ценах на арбузы всегда нулевые — не засоряем ими экран
  const rounded = Math.round(n);
  // Разделитель разрядов — неразрывный узкий пробел (U+202F): обычный
  // пробел позволяет сумме переноситься по строкам посередине числа.
  return rounded
    .toLocaleString(locale === 'uz' ? 'uz-UZ' : 'ru-RU')
    .replace(/\s/g, '\u202F');
}

/**
 * Площадь зоны доставки.
 *
 * Мелкие зоны — с десятой долей: разница между 0.8 и 1.4 км² для исполнителя
 * существенна, а между 44 и 44.6 км² — уже нет.
 */
export function formatArea(km2: number, locale: Locale): string {
  const value = km2 < 10 ? km2.toFixed(1) : String(Math.round(km2));
  return `${value.replace('.', ',')} ${makeT(locale)('zoneAreaUnit')}`;
}

export function formatWeight(min: string | null, max: string | null): string | null {
  if (!min && !max) return null;
  const f = (v: string) => String(Number(v)).replace('.', ',');
  if (min && max) return `${f(min)}–${f(max)}`;
  return f((min ?? max) as string);
}
